import pako from "pako";
import { DateTime } from "luxon";
import { parse } from "papaparse";
import chardet from "chardet";
import { v4 as UUID_V4 } from "uuid";

import { TIME_FORMATS, MYSQL_TIME_FORMATS } from "./Data/FormatDetection/Time";
import { DATETIME_FORMATS, MYSQL_DATETIME_FORMATS } from "./Data/FormatDetection/DateTime";
import { DATE_FORMATS, MYSQL_DATE_FORMATS } from "./Data/FormatDetection/Date";

import store from "../store";
import { allPastelColors } from "./Data/AllPastelColors";

const setPrevState = (setObj, newSetObj) => {
  setObj((Prev) => ({ ...Prev, ...newSetObj }));
};

const utf32Decode = (bytes) => {
  try {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    let result = "";
    for (let i = 0; i < bytes.length; i += 4) {
      const codePoint = view.getInt32(i, false);
      result += String.fromCodePoint(codePoint);
    }
    return result;
  } catch (error) {
    return "Invalid Data: Unable to parse with UTF-32";
  }
};

/**
 * Convert date to different timezone and get formatted date
 * @param {Date | string} date UTC Date
 * @param {Object} options Conversion options
 * @param {boolean} options.needFormatted True: Return formatted date string (MM/DD/YYYY hh:mm:ss)
 * @param {boolean} options.needDateObj True: Return date object in the specified timezone
 * @returns {string | Date}
 */
const convertTimezone = (date, { needFormatted = false, needDateObj = false } = {}) => {
  if ([undefined, null, "", "-", "N/A"].includes(date)) return "";

  const givenDate = new Date(date); // UTC date
  if (givenDate == "Invalid Date") return "";

  const timeZone = store.getState()?.user?.currentUser?.UserTimezone || "Etc/UTC";
  const locale = store.getState()?.user?.currentUser?.UserLocale || "en-US";

  // Convert the given UTC date to the user's timezone with en-US locale to avoid issues
  if (needDateObj) return new Date(givenDate.toLocaleString("en-US", { timeZone }));

  // Convert date to the specified timezone
  const options = {
    timeZone,
    timeZoneName: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false, // Set to false for 24-hour format
  };

  const formatter = new Intl.DateTimeFormat([locale, "en-US"], options);

  if (!formatter) return "";

  if (needFormatted) {
    // Get the formatted date parts
    const parts = formatter.formatToParts(givenDate);

    // Create an object to store the formatted date and time parts
    const dateParts = {};
    parts.forEach(({ type, value }) => {
      if (type !== "literal") {
        dateParts[type] = value;
      }
    });

    // Construct the formatted date string
    return `${dateParts["month"]}/${dateParts["day"]}/${dateParts["year"]} ${dateParts["hour"]}:${dateParts["minute"]}:${dateParts["second"]}`;
  }

  // Return date formatted based on locale
  return formatter.format(givenDate);
};

// Function to check if the given bytes match the magic number
const isMagicNumberMatch = (bytes, magicNumber) => {
  for (let i = 0; i < magicNumber.length; i++) {
    if (bytes[i] !== magicNumber[i]) return false;
  }
  return true;
};

const isCompressionGZ = (uploadedFile) =>
  new Promise((resolve) => {
    let chunkReaderBlock = null;
    const readEventHandler = function (e) {
      if (e.target.error == null) {
        const chunkBlock = new Uint8Array(e.target.result);
        const bytes = chunkBlock.slice(0, 8);
        resolve(isMagicNumberMatch(bytes, new Uint8Array([31, 139])) || uploadedFile.name.endsWith(".gz"));
      } else resolve(false);
    };

    chunkReaderBlock = function (_offset, length, _file) {
      const r = new FileReader();
      const blob = _file.slice(_offset, length + _offset);
      r.onload = readEventHandler;
      // Read the beginning of the file for magic number check
      r.readAsArrayBuffer(blob);
    };

    chunkReaderBlock(0, 20, uploadedFile);
  });

/**
 * Parses files into chunks for reading
 * @param {string} file
 * @param {function} callback
 */
const parseFile = (file, callback, needFileProperties = false, encoding = "UTF-8") => {
  const fileSize = file.size;
  let chunkSize = 100 * 1024; // bytes (Making this chunk to around 1MB so that even files with more columns like 300 or more can also be covered)
  let offset = 0;
  let chunkReaderBlock = null;
  let fileProperties = {};

  // Handling read chunk event
  const readEventHandler = function (evt) {
    if (evt.target.error == null) {
      const chunkBlock = new Uint8Array(evt.target.result);
      const bytes = chunkBlock.slice(0, 8);

      //detect encoding wether need file properties or not to correctly decode the string
      let detectedEncoding = chardet.detect(chunkBlock, { sampleSize: 300 });
      fileProperties.encoding = detectedEncoding;
      const parseWithEncoding = needFileProperties ? detectedEncoding : encoding;
      let textDecoder = null;
      let isUTF32Encoded = false;
      if (
        parseWithEncoding.toLowerCase() === "utf-32" ||
        parseWithEncoding.toLowerCase() === "utf-32le" ||
        parseWithEncoding.toLowerCase() === "utf-32be"
      ) {
        fileProperties.encoding = "utf-32";
        isUTF32Encoded = true;
      } else textDecoder = new TextDecoder(parseWithEncoding);

      // Determine file type based on magic numbers
      if (isMagicNumberMatch(bytes, new Uint8Array([80, 65, 82, 49])) || file.name.endsWith(".parquet")) {
        if (needFileProperties) fileProperties = { ...fileProperties, ...identifyFileProperties("", "parquet") };
        return callback("", () => {}, fileProperties);
      } else if (isMagicNumberMatch(bytes, new Uint8Array([31, 139])) || file.name.endsWith(".gz")) {
        // for handling gz file issue
        let inflateOutput = null;
        let parsedString = null;

        if (chunkSize >= file.size) inflateOutput = new pako.inflate(new Uint8Array(chunkBlock));
        else {
          inflateOutput = new pako.Inflate({});
          // Push the compressed data to the inflate instance
          inflateOutput.push(new Uint8Array(chunkBlock));
        }

        // getting output as unit8array based on inflate result
        if (!(inflateOutput instanceof Uint8Array))
          inflateOutput = new Uint8Array([].concat(...inflateOutput.chunks.map((arr) => [...arr])));

        if (needFileProperties) {
          const match = file.name.match(/^(.+)\.gz$/i);
          const extension = match ? match[1].substring(match[1].lastIndexOf(".") + 1).toLowerCase() : "";

          parsedString = isUTF32Encoded ? utf32Decode(inflateOutput) : textDecoder.decode(inflateOutput);

          fileProperties = {
            ...fileProperties,
            ...identifyFileProperties(parsedString, extension === "parquet" ? "parquet" : "csv"),
            isCompressionGZ: true,
          };
        }
        if (parsedString === null)
          parsedString = isUTF32Encoded ? utf32Decode(inflateOutput) : textDecoder.decode(inflateOutput);

        //callback for handling read chunk
        //also returning callback for readmore
        callback(parsedString, () => chunkReaderBlock(offset, chunkSize, file), fileProperties);
      } else {
        offset += chunkSize;

        //callback for handling read chunk
        //also returning callback for readmore
        const csvString = isUTF32Encoded ? utf32Decode(chunkBlock) : textDecoder.decode(chunkBlock);
        if (needFileProperties) fileProperties = { ...fileProperties, ...identifyFileProperties(csvString, "csv") };
        callback(csvString, () => chunkReaderBlock(offset, chunkSize, file), fileProperties);
      }
    } else {
      if (needFileProperties) fileProperties = { ...fileProperties, ...identifyFileProperties("", "csv") };
      return callback("", () => {}, fileProperties);
    }
    if (offset >= fileSize) {
      if (!needFileProperties) return callback("", () => {}, fileProperties);
      return;
    }
  };

  //generating reader
  chunkReaderBlock = function (_offset, length, _file) {
    const r = new FileReader();
    const blob = _file.slice(_offset, length + _offset);

    r.onload = readEventHandler;

    // Read the beginning of the file for magic number check
    r.readAsArrayBuffer(blob);
  };

  //start the read with the first block
  chunkReaderBlock(offset, chunkSize, file);
};

/**
 * identify source file property
 * @param {String} parsedChunk chunk of data as a string from file
 * @param {String} fileType Type of file to include in metadata
 * @returns {Object} source file properties => encoding, delimiter, textQualifier
 */
const identifyFileProperties = (parsedChunk = "", fileType = "csv") => {
  let result = { fileType };
  if (parsedChunk === "") return result;

  parse(parsedChunk, {
    preview: 5,
    complete: (results) => (result.delimiter = results.meta.delimiter),
    error: (error) => console.log(error),
  });

  const textQualifierRegex = /("(?:[^"\\]|\\.)*")|('(?:[^'\\]|\\.)*')/g;

  let textQualifierMatch;
  const maxMatches = 15;
  let matchCount = 0;
  let doubleQuotes = 0;
  let singleQuotes = 0;

  while ((textQualifierMatch = textQualifierRegex.exec(parsedChunk)) !== null && matchCount < maxMatches) {
    if (textQualifierMatch[0].startsWith('"')) doubleQuotes += 1;
    else singleQuotes += 1;
    matchCount++;
  }
  result.textQualifier = doubleQuotes > singleQuotes ? '"' : doubleQuotes < singleQuotes ? "'" : "none";
  result.nullValue = result.textQualifier === "none" ? undefined : result.textQualifier === "'" ? "''" : '""';

  return result;
};

/**
 * Generate N number of unique random colors except white along with suitable textcolor (white || black)
 * @param {Integer} n range of colors to generate
 * @returns {Array}
 */
const generateUniqeRandomColors = (n) => {
  let colorArr = [];
  // To generate random R,G,B value except White
  const randomRgb = function () {
    const r = Math.floor(Math.random() * 256); //NOSONAR
    const g = Math.floor(Math.random() * 256); //NOSONAR
    const b = Math.floor(Math.random() * 256); //NOSONAR

    // for excluding repeated color and white color
    const isColorInArr = colorArr.some((color) => color.r === r && color.g === g && color.b === b);
    if ((r === 0 && g === 0 && b === 0) || isColorInArr) randomRgb();
    else return [r, g, b];
  };

  // To check BG color is light or dark and based on that text color will be define
  const colourIsLight = function (r, g, b) {
    // Counting the perceptive luminance
    // human eye favors green color...
    const a = 1 - (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return a < 0.5;
  };

  // Convert RGB to Hex
  const rgbToHex = (r, g, b) => "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");

  for (let i = 0; i < n; i++) {
    const bgColor = randomRgb();
    const textColor = colourIsLight(bgColor[0], bgColor[1], bgColor[2]) ? "black" : "white";
    const hex = rgbToHex(bgColor[0], bgColor[1], bgColor[2]);

    colorArr.push({ r: bgColor[0], g: bgColor[1], b: bgColor[2], hex, textColor });
  }

  return colorArr;
};

const getBackgroundColor = (index) => {
  if (index < allPastelColors.length) return allPastelColors[index];
  const randomColor = generateUniqeRandomColors(1) ?? {};
  return randomColor.hex;
};

/**
 * Based on the data passed it will guess all data type of all the columns
 * @param {Array} headerField Name of header fields
 * @param {Array} dataLines First 5 Rows of data from CSV file selected
 * @returns {Array}
 */
const identifyDataTypeAndFormat = (headerField, dataLines, destinationType = "snowflake") => {
  const columnsValues = {};
  const columnsTypes = {};
  headerField.forEach((field) => {
    columnsTypes[field] = { dataType: "String", format: null };
    columnsValues[field] = [];
  });

  headerField.forEach((field, index) => {
    dataLines.forEach((data) => {
      if (data?.length !== headerField?.length) return;
      columnsValues[field] = [...columnsValues[field], data[index]];
    });
  });

  headerField.forEach((field) => {
    const data = columnsValues[field] || [];
    let dateTimeFormat = "";
    let dateFormat = "";
    let timeFormat = "";
    let startsWithZeroCount = 0;

    const allDecimalPoints = [];
    const allStringLengths = [];

    const dataTypeCounts = {
      emptyCount: 0,
      Boolean: 0,
      String: 0,
      Number: 0,
      Integer: 0,
      DateTime: 0,
      Date: 0,
      Time: 0,
    };

    const increaseCount = (dataType) => (dataTypeCounts[dataType] += 1);

    for (let rawItem of data) {
      rawItem = rawItem?.toString() || "";
      allStringLengths.push(rawItem.length);
      const item = rawItem.trim();

      // Check for empty string first
      if (item === "" || item.trim() === "") {
        increaseCount("emptyCount");
        continue;
      }

      // Not empty, Check for boolean first
      if (["true", "false"].includes(item.toLowerCase())) {
        increaseCount("Boolean");
        continue;
      }

      // Not boolean, check for Number and Integers
      if (!isNaN(item) && item !== "") {
        if (item.startsWith("0") && !/^[0.]*\.?[0.]?$/.test(item)) startsWithZeroCount += 1;

        const decimalIndex = item.indexOf(".");
        if (decimalIndex !== -1) {
          allDecimalPoints.push(item.length - decimalIndex - 1);
          increaseCount("Number");
        } else increaseCount("Integer");
        continue;
      }

      const _dateTimeFormats = destinationType === "mysql" ? MYSQL_DATETIME_FORMATS : DATETIME_FORMATS;
      const _dateFormats = destinationType === "mysql" ? MYSQL_DATE_FORMATS : DATE_FORMATS;
      const _timeFormats = destinationType === "mysql" ? MYSQL_TIME_FORMATS : TIME_FORMATS;

      // Not Number and Integers, check for DateTime
      const isDateTime = isValidFormat(Object.keys(_dateTimeFormats), item);
      if (isDateTime.valid) {
        dateTimeFormat = _dateTimeFormats[isDateTime.format];
        increaseCount("DateTime");
        continue;
      }

      // Not DateTime, check for Date
      const isDate = isValidFormat(Object.keys(DATE_FORMATS), item);
      if (isDate.valid) {
        dateFormat = _dateFormats[isDate.format];
        increaseCount("Date");
        continue;
      }

      // Not Date, check for Time
      const isTime = isValidFormat(Object.keys(_timeFormats), item);
      if (isTime.valid) {
        timeFormat = _timeFormats[isTime.format];
        increaseCount("Time");
        continue;
      }

      // At this point it is String
      increaseCount("String");
    }

    let max = { key: "String", count: 0 };
    columnsTypes[field] = {
      dataType: "String",
      format: null,
      length: Math.max(...allStringLengths, 1),
    };

    const typesDetected = Object.entries(dataTypeCounts)
      .filter(([key, value]) => {
        if (key !== "emptyCount" && value > max.count) max = { key, count: value };
        return value > 0 && key !== "emptyCount";
      })
      .map(([key]) => key);

    if (
      !typesDetected.includes("String") &&
      (typesDetected.length === 1 || (typesDetected.includes("Number") && typesDetected.includes("Integer")))
    ) {
      if (typesDetected.includes("Boolean") && typesDetected.length === 1) {
        columnsTypes[field] = { dataType: "Boolean", format: null, length: "" };
      } else if (typesDetected.includes("Number") && startsWithZeroCount === 0) {
        const maxDecimal = Math.max(...allDecimalPoints, 1);
        columnsTypes[field] = { dataType: "Number", format: null, length: `20,${maxDecimal < 10 ? maxDecimal : 10}` };
      } else if (typesDetected.includes("Integer") && startsWithZeroCount === 0) {
        columnsTypes[field] = { dataType: "Integer", format: null, length: "" };
      } else if (typesDetected.includes("DateTime")) {
        columnsTypes[field] = { dataType: "DateTime", format: dateTimeFormat, length: "" };
      } else if (typesDetected.includes("Date")) {
        columnsTypes[field] = { dataType: "Date", format: dateFormat, length: "" };
      } else if (typesDetected.includes("Time")) {
        columnsTypes[field] = { dataType: "Time", format: timeFormat, length: "" };
      }
    }

    // At this point is no other data type detected then calculate the string length
    if (columnsTypes[field].dataType === "String") {
      const previewCount = store.getState()?.config?.previewCount || 100;
      const attributeDataTypes = store.getState()?.config?.attributeDataTypes || [];
      const stringOptions = attributeDataTypes.find(({ type }) => type === "String")?.options || [];
      const stringValues = stringOptions.map(({ value }) => value) || [];

      if (dataTypeCounts["emptyCount"] === data.length) columnsTypes[field]["length"] = stringValues?.[0] || 50;
      else {
        /*
        To avoid borderline risk this custom logic is written
        if total data is less than preview count then find the greater or equal value from length array
        else returning the max * 3 size
        */
        const foundLength = stringValues.find((val) => {
          if (data.length < previewCount) return val >= columnsTypes[field]["length"];
          return val >= columnsTypes[field]["length"] * 3;
        });
        // if no length found that means max length available (currently 5000)
        columnsTypes[field]["length"] = foundLength || stringValues[stringValues.length - 1];
      }
    }
  });

  return columnsTypes;
};

// For given formats, if given data is valid or not
const isValidFormat = (formats, stringDate) => {
  let date = null;

  for (const format of formats) {
    date = DateTime.fromFormat(stringDate, format);
    if (date.invalid === null) return { valid: true, format: format };
  }

  return { valid: false, format: null };
};

//Comparing Resursively Deep copy
const isEqual = (a, b, exceptionFields = []) => {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (!a || !b || (typeof a !== "object" && typeof b !== "object")) return a === b;
  if (a === null || a === undefined || b === null || b === undefined) return false;
  if (a.prototype !== b.prototype) return false;

  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    return a.every((k, i) => isEqual(a[i], b[i], exceptionFields));
  } else {
    const exceptionSet = new Set(exceptionFields);
    const countFilteredKeys = (obj) => Object.keys(obj).reduce((count, key) => count + !exceptionSet.has(key), 0);
    if (countFilteredKeys(a) !== countFilteredKeys(b)) return false;

    return Object.keys(a).every((k) => {
      if (exceptionFields.length && exceptionFields.includes(k)) return true;
      return isEqual(a[k], b[k], exceptionFields);
    });
  }
};

const formatDate = (date, displayFormat = false, dateOnly = false) => {
  let d = new Date(date),
    month = "" + (d.getMonth() + 1),
    day = "" + d.getDate(),
    year = d.getFullYear(),
    hours = "" + d.getHours(),
    minutes = "" + d.getMinutes(),
    seconds = "" + d.getSeconds();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;
  if (hours.length < 2) hours = "0" + hours;
  if (minutes.length < 2) minutes = "0" + minutes;
  if (seconds.length < 2) seconds = "0" + seconds;

  const displayDate = displayFormat ? `${[month, day, year].join("/")}` : `${[year, month, day].join("-")}`;

  if (dateOnly) return displayDate;
  return `${displayDate} ${[hours, minutes, seconds].join(":")}`;
};

const calculateTimeFromMS = (TimeInMS, bufferBefore, bufferTime) => {
  let formattedTime;

  TimeInMS = isNaN(TimeInMS) ? 0 : Math.max(0, TimeInMS);

  // adding `bufferTime` if TimeInMS is less than `bufferBefore`
  if (bufferBefore && bufferTime && TimeInMS <= bufferBefore) TimeInMS += bufferTime;

  if (TimeInMS < 60000) {
    formattedTime = `${Math.ceil(TimeInMS / 1000)} sec`;
  } else if (TimeInMS < 3600000) {
    const minutes = Math.floor(TimeInMS / 60000);
    const seconds = Math.floor((TimeInMS % 60000) / 1000);
    formattedTime = `${minutes}.${seconds} min`;
  } else {
    const hours = Math.floor(TimeInMS / 3600000);
    const minutes = Math.floor((TimeInMS % 3600000) / 60000);
    formattedTime = `${hours}.${minutes} hr`;
  }

  return formattedTime;
};

const openInNewTab = (url) => {
  window.open(url, "_blank");
};

// Function to check if a color is dark
const isColorDark = (color) => {
  const r = parseInt(color.substring(1, 3), 16);
  const g = parseInt(color.substring(3, 5), 16);
  const b = parseInt(color.substring(5, 7), 16);

  // brightness
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Return true if the brightness is less than a threshold
  return brightness < 128;
};

//Hex color #xxxxxx to rgba(xx,xx,xx,1);
const hexToRGBA = (hex, alpha) => {
  hex = hex.replace("#", "");

  // Parse hex values to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Construct the RGBA color string
  return `rgba(${r},${g},${b},${alpha})`;
};

//Lighten color
//same as above but returns rgb
function lightenColor(color, percent) {
  percent = 1 - percent;

  let r = parseInt(color.substr(1, 2), 16);
  let g = parseInt(color.substr(3, 2), 16);
  let b = parseInt(color.substr(5, 2), 16);

  if (percent < 0) {
    r = (1 + percent) * r;
    g = (1 + percent) * g;
    b = (1 + percent) * b;
  } else {
    r = (1 - percent) * r + percent * 255;
    g = (1 - percent) * g + percent * 255;
    b = (1 - percent) * b + percent * 255;
  }

  return `rgb(${r},${g},${b})`;
}

const handleQueryResponse = (response, setState) => {
  if (response.success) {
    let error = "";
    let columnArr = [];
    if (!response.error) {
      if (response.data.columns.length) {
        response.data.columns.forEach((col) => {
          columnArr.push({
            name: col,
            align: "left",
            minWidth: 300,
            maxWidth: 300,
          });
        });
      }
    } else {
      error = response.error;
    }

    setState({
      id: 1,
      queryResultColumns: columnArr,
      queryResult: response?.data?.result || [],
      queryError: error,
      queryResultLoding: false,
    });
  } else setState({ id: 1, queryResultColumns: [], queryResult: [], queryError: "", queryResultLoding: false });

  setPrevState(setState, { queryResultLoding: false });
};

const removeQueryComments = (query = "") => {
  return query
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace(/--.*$/gm, "")
    .replace(/\r/g, "")
    .trim();
};

/**
 * Cleans one query line and retrive executable part from it if any
 * @param {String} line
 * @returns
 */
const cleanQueryLine = (line) => {
  return line
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace("\n", "")
    .split("--")[0];
};

/**
 * Cleans query line one by to remove multiple comment and single comment from array
 * @param {Array} allLines
 * @returns
 */
const getQueryLines = (allLines) => {
  const singleCmtRegex = /--.*$/gm;
  const multiLineStartRegex = /\/\*/;
  const multiLineEndRegex = /\*\//;

  let inMultiLineComment = false;

  const terminationIndexes = [];
  let current = "";
  let insideDollarBlock = false;
  let insideBlockComment = false;
  let insideSingleQuotes = false;
  let insideDoubleQuotes = false;

  const isNeutral = () => !insideBlockComment && !insideSingleQuotes && !insideDoubleQuotes;

  const lines = allLines.map((line, index) => {
    // To get terminating index
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextTwo = line.slice(i, i + 2);

      // Toggle dollar-quoted block
      if (nextTwo === "$$" && isNeutral()) {
        insideDollarBlock = !insideDollarBlock;
        current += "$$";
        i++;
        continue;
      }

      // Start multi-line comment
      if (nextTwo === "/*" && !insideDollarBlock && isNeutral()) {
        insideBlockComment = true;
        current += "/*";
        i++;
        continue;
      }

      // End multi-line comment
      if (nextTwo === "*/" && insideBlockComment) {
        insideBlockComment = false;
        current += "*/";
        i++;
        continue;
      }

      // Start single-line comment
      // Like backend there is no need to handle (insideLineComment) variable for single line as we will go here line by line instead of whole query
      if (nextTwo === "--" && !insideDollarBlock && isNeutral()) {
        current += "--" + line.slice(i + 2); // Include the rest of the comment
        break; // End processing this line
      }

      // Toggle single quotes (handle escaped quotes: '')
      if (char === "'" && !insideDoubleQuotes && !insideBlockComment && !insideDollarBlock) {
        if (insideSingleQuotes && line[i + 1] === "'") {
          current += "''";
          i++; // Skip the escaped quote
          continue;
        }
        insideSingleQuotes = !insideSingleQuotes;
      }

      // Toggle double quotes (handle escaped quotes: "")
      if (char === '"' && !insideSingleQuotes && !insideBlockComment && !insideDollarBlock) {
        if (insideDoubleQuotes && line[i + 1] === '"') {
          current += '""';
          i++; // Skip the escaped quote
          continue;
        }
        insideDoubleQuotes = !insideDoubleQuotes;
      }

      // Handle semicolon
      if (char === ";" && !insideDollarBlock && isNeutral()) {
        if (checkNotNullQuery(current)) {
          terminationIndexes.push(index);
        }
        current = "";
      } else {
        current += char;
      }
    }

    if (inMultiLineComment) {
      if (multiLineEndRegex.test(line)) {
        inMultiLineComment = false;
        return cleanQueryLine(line.replace(/[\s\S]*?\*\//, ""));
      }

      // Still inside multi-line comment
      return "";
    }

    // Single-line comment
    if (singleCmtRegex.test(line)) return cleanQueryLine(line);

    const startMatch = multiLineStartRegex.test(line);
    const endMatch = multiLineEndRegex.test(line);

    if (startMatch) {
      if (endMatch) return cleanQueryLine(line);
      else {
        inMultiLineComment = true;
        return "";
      }
    }

    return cleanQueryLine(line);
  });

  return { lines, terminationIndexes };
};

const checkNotNullQuery = (str) => {
  const removeMultiComment = str.replace(/\/\*[\s\S]*?\*\//g, "");
  const removeSingleComment = removeMultiComment.replace(/--.*?(?=\n|$)/g, "");
  const removeNewLineChar = removeSingleComment.replace("\n", "");
  return removeNewLineChar.trim() !== "";
};

const getTabQuery = (obj) => {
  let selectedQuery = "";
  let { query, queryRef, cursorPosition } = obj;

  let tabQuery = query;
  selectedQuery = queryRef.current.editor.getSession().doc.getTextRange(queryRef.current.editor.selection.getRange());

  // If selected query than return it
  if (!!selectedQuery) return selectedQuery;

  const cursorAtEmptyLine = queryRef.current.editor.getSession().getLine(cursorPosition)?.trim() === "";
  let finishedAtAboveLine = cursorPosition === 0;

  for (let line = cursorPosition; line > 0; line--) {
    let prevLine = cleanQueryLine(queryRef.current.editor.getSession().getLine(line - 1));

    if (prevLine !== "") {
      if (prevLine.includes(";")) finishedAtAboveLine = true;
      break;
    }

    if (line === 1) finishedAtAboveLine = true;
  }

  if ((cursorAtEmptyLine && !finishedAtAboveLine) || !cursorAtEmptyLine) {
    let runFromLine = 0;
    let runTillLine = [];

    const allLines = queryRef.current.editor.getSession().doc.getAllLines();
    const { lines: allCleanedLines, terminationIndexes } = getQueryLines(allLines);

    if (terminationIndexes.length > 1) {
      terminationIndexes.forEach((row) => {
        if (row < cursorPosition) runFromLine = row + 1;
        else runTillLine.push(row);
      });

      if (runTillLine.length === 0) runTillLine.push(allCleanedLines.length - 1);

      let linesToInclude = allLines.slice(runFromLine, runTillLine[0] + 1);

      if (linesToInclude.every((line) => line.trim().length === 0)) linesToInclude = allLines;

      tabQuery = linesToInclude.join("\n");
    }
  } else {
    const allLines = queryRef.current.editor.getSession().doc.getAllLines();
    tabQuery = allLines.join("\n");
  }

  return tabQuery;
};

/**
 * Check if provided query is select query or non data manipulating query
 * @param {String} finalQuery
 * @returns
 */
const checkIfSelectQuery = (finalQuery, showAllowed = true) => {
  // Remove carriage return and comment
  const query = finalQuery
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace(/--.*$/gm, "")
    .replace(/\r/g, "")
    .trim();

  if (!query) return true; // empty query

  //removing ending ; for spliting
  let queries = query.split(";");
  if (query.endsWith(";")) queries.pop();

  const regex = showAllowed
    ? /^(WITH\s+[\s\S]+?\s+SELECT\s+[\s\S]+?;?|SHOW\s+[\s\S]+?;?|SELECT\s+[\s\S]+?;?|USE\s+[\s\S]+?;?)$/is
    : /^(WITH\s+[\s\S]+?\s+SELECT\s+[\s\S]+?;?|SELECT\s+[\s\S]+?;?|USE\s+[\s\S]+?;?)$/is;

  return !queries.some((qry) => !regex.test(qry.trim()));
};

const checkIsSchemaExist = (schemasByDB, database, schema) => {
  // If user do not have access to previously selected DB or schema or it has been deleted
  // then we will clear in initalState and show user an error
  return !(!schemasByDB?.[database] || !schemasByDB?.[database]?.some(({ SCHEMA_NAME }) => SCHEMA_NAME === schema));
};

const checkIsClusterExist = (clusterList, cluster) => {
  // If user do not have access to previously selected cluster
  // then we will clear in initalState and show user an error
  return !!clusterList.some(({ value }) => value?.toLowerCase() === cluster?.toLowerCase());
};

const arrangeSchema = (data, setState = false) => {
  if (Object.keys(data)?.length) {
    let schemasByDB = [];
    Object.keys(data).forEach((DB) => {
      schemasByDB = [
        ...schemasByDB,
        ...data[DB].map(({ SCHEMA_NAME }) => {
          return {
            // key: `${DB}.${SCHEMA_NAME}`,
            title: SCHEMA_NAME,
            groupBy: DB,
            // value: `${DB}.${SCHEMA_NAME}`,
          };
        }),
      ];
    });

    if (!setState) return schemasByDB;
    setState(schemasByDB);
    return data;
  }
  return !setState ? [] : {};
};

const showLastRunText = (status, statusText, lastRunTimeInMS, formattedDate) => {
  if (!status && !statusText) return "";
  if (status === "terminating") return statusText;
  let runDetail = "";
  if ((status === "success" || status === "warning") && lastRunTimeInMS >= 0)
    runDetail = `ran for ${calculateTimeFromMS(lastRunTimeInMS, 30000, 20000)}`;
  if (formattedDate) runDetail += `${runDetail ? " on" : "on"} ${formattedDate}`;
  if (runDetail) runDetail = `(${runDetail})`;

  return `${statusText} ${runDetail}`?.trim();
};

const getStoredValue = (key, initialValue, useLocal = true) => {
  try {
    const item = useLocal ? window.localStorage.getItem(key) : window.sessionStorage.getItem(key);
    return item ? JSON.parse(item) : initialValue;
  } catch (error) {
    console.error(`Error reading ${useLocal ? "localStorage" : "sessionStorage"} key "${key}":`, error);
    return initialValue;
  }
};

const setStorageValue = (key, initialValue, value, useLocal = true) => {
  try {
    const valueToStore = value instanceof Function ? value(getStoredValue(key, initialValue)) : value;
    if (useLocal) window.localStorage.setItem(key, JSON.stringify(valueToStore));
    else window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
  } catch (error) {
    console.error(`Error setting ${useLocal ? "localStorage" : "sessionStorage"} key "${key}":`, error);
  }
};

/**
 * Find recursive relations between import export objects based on category
 * @param {Array<string>} uuidList List of UUIDs.
 * @param {Array<Object>} dataSources Array of data source objects.
 * @param {Array<Object>} entities Array of entity objects.
 * @param {Array<Object>} connections Array of connection objects.
 * @param {Array<Object>} pipelines Array of pipeline objects.
 * @returns {Array<Object>}
 */
const findRelations = (uuidList, dataSources, entities, connections, pipelines) => {
  const result = {
    datasources: [],
    entities: [],
    connections: [],
    pipelines: [],
  };

  const visited = new Set();

  const searchCategory = (uuid, categoryItems, categoryName) => {
    for (const item of categoryItems) {
      if (item.value === uuid) {
        result[categoryName].push(uuid);
        if (item.relations) {
          item.relations.forEach((relationUuid) => explore(relationUuid));
        }
        return true; // Stop: UUID found
      }
    }
    return false; // UUID not found
  };

  const explore = (uuid) => {
    if (visited.has(uuid)) return;
    visited.add(uuid);

    // Search in each category one by one
    if (searchCategory(uuid, connections, "connections")) return;
    if (searchCategory(uuid, entities, "entities")) return;
    if (searchCategory(uuid, dataSources, "datasources")) return;
    if (searchCategory(uuid, pipelines, "pipelines")) return;
  };

  // Start with the provided input UUIDs
  uuidList.forEach((uuid) => explore(uuid));

  return result;
};

const generateNewCounterName = (validationList, prefix = "", seperator = "_", usebaseName = true) => {
  let counter = usebaseName ? 0 : 1;
  const generateNewName = () => `${prefix}${seperator}${counter}`;
  let newName = usebaseName ? prefix : generateNewName();

  while (validationList.includes(newName.toLowerCase())) {
    counter += 1;
    newName = generateNewName();
  }

  return newName;
};

const capitalizeWords = (str) => {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const isEmptyObject = (obj) => {
  for (let key in obj) return false;
  return true;
};

const getUUID = () => UUID_V4();

const base64Encode = async (str) => {
  // Equivelent of this in Node.js will be
  // return Buffer.from(str, "utf8").toString("base64");

  const utf8Bytes = new TextEncoder().encode(str);
  const blob = new Blob([utf8Bytes]);
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const base64Decode = async (b64) => {
  // Equivelent of this in Node.js will be
  // return Buffer.from(b64, "base64").toString("utf8");

  const dataUrl = `data:text/plain;base64,${b64}`;
  const response = await fetch(dataUrl);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsText(blob);
  });
};

export {
  setPrevState,
  parseFile,
  convertTimezone,
  isCompressionGZ,
  generateUniqeRandomColors,
  identifyDataTypeAndFormat,
  isEqual,
  formatDate,
  calculateTimeFromMS,
  openInNewTab,
  isColorDark,
  hexToRGBA,
  lightenColor,
  handleQueryResponse,
  getTabQuery,
  checkIfSelectQuery,
  removeQueryComments,
  checkIsSchemaExist,
  checkIsClusterExist,
  arrangeSchema,
  showLastRunText,
  getStoredValue,
  setStorageValue,
  findRelations,
  generateNewCounterName,
  getUUID,
  capitalizeWords,
  getBackgroundColor,
  isEmptyObject,
  base64Encode,
  base64Decode,
};
